import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/store/store";

/**
 * Where the session's selection came from. Both origins share every session
 * behaviour (local-only segment edits, re-assertion after a catalog refresh);
 * they differ only in what the user is told, since "shared" is a lie on a view
 * the caller saved themselves.
 */
export type SmartLinkOrigin = "share" | "view";

export interface SmartLinkState {
  active: boolean;
  origin: SmartLinkOrigin;
  snapshotApplied: string[] | null;
  linkSegments: string[] | null;
  enteredPath: string | null;
}

const initialState: SmartLinkState = {
  active: false,
  origin: "share",
  snapshotApplied: null,
  linkSegments: null,
  enteredPath: null,
};

const smartLinkSlice = createSlice({
  name: "smartLink",
  initialState,
  reducers: {
    enterSmartLink: (
      state,
      action: PayloadAction<{
        snapshotApplied: string[];
        linkSegments: string[];
        enteredPath: string;
        origin?: SmartLinkOrigin;
      }>,
    ) => {
      if (!state.active) {
        state.snapshotApplied = action.payload.snapshotApplied;
        state.enteredPath = action.payload.enteredPath;
        state.origin = action.payload.origin ?? "share";
      }
      state.active = true;
      state.linkSegments = action.payload.linkSegments;
    },
    setLinkSegments: (state, action: PayloadAction<string[]>) => {
      state.linkSegments = action.payload;
    },
    exitSmartLink: () => initialState,
  },
});

export const { enterSmartLink, setLinkSegments, exitSmartLink } = smartLinkSlice.actions;
export const selectSmartLink = (state: RootState): SmartLinkState => state.smartLink;
export default smartLinkSlice.reducer;
